import { LinearTransform, Point, Rect } from '@st/geom'
import { defineModule, Ref } from '@st/redux'
import { deepEqual } from '@st/redux/src/deep-equal'
import { jsonMapPatch } from '@st/util/json-diff'
import { AnnotationPlugin } from './annotation-plugin'
import { PageStructure } from './models'

export type AnnotationBase = {
  id: string
  time: string
  type: string
  pageNumber: number
  bounds: Rect
}

/**
 * A draft may either be a full annotation (for optimistic create/update)
 * **or** the literal `null` when the user has requested a deletion that
 * the server has not confirmed yet.
 * Using this "tomb‑stone" keeps the optimistic‑UI model uniform: a missing
 * annotation is always represented by its id in `annotationDrafts`.
 */
export type AnnotationDraft = AnnotationBase | null

export type ToolStateBase = { name: string; settings: Record<string, any> }

export type DocumentViewerState = {
  zoom: number
  annotations: Record<string, AnnotationBase>
  annotationDrafts: Record<string, AnnotationDraft>

  /**
   * The currently selected tool, ex, "rectangle"
   * This is distinct from the annotation type itself which might happen to have the same name.
   *
   * This can change either by an explicit selection (clicking on the Rectangle button)
   * or automatically (when a rectangle annotation is selected)
   *
   * The corresponding state of the tool is stored at `toolStates[selectedToolName]`
   */
  selectedToolName: string
  toolStates: Record<string, ToolStateBase>
  selectionState:
    | { type: 'none' }
    | { type: 'selected'; annotationId: string }
    | { type: 'editing'; annotationId: string }

  plugins: Ref<Array<AnnotationPlugin<AnnotationBase, ToolStateBase>>>
}

export type ElementStatus = 'none' | 'selected' | 'editing'

export type DocumentViewerMessage =
  | { type: 'zoom'; level: number }
  | { type: 'scrollToPage'; pageNumber: number }
  | { type: 'deselectAnnotation' }
  | { type: 'selectAnnotation'; annotationId: string }
  | { type: 'scrollToAnnotation'; annotationId: string }
  | { type: 'selectTool'; toolName: ToolStateBase['name'] }
  | { type: 'beginEditingAnnotation'; annotationDraft: AnnotationBase }
  | { type: 'createAnnotationDraft'; annotationDraft: AnnotationBase; elementStatus: ElementStatus }
  | { type: 'updateAnnotationDraft'; annotationDraft: AnnotationBase }
  | { type: 'discardAnnotationDraft'; annotationId: string }
  | { type: 'createAnnotation'; annotationDraft: AnnotationBase; edit?: boolean }
  | { type: 'updateAnnotation'; annotationDraft: AnnotationBase }
  | { type: 'deleteAnnotation'; annotationId: string }
  | { type: 'updateTool'; tool: ToolStateBase }
  | { type: 'remoteAnnotationsUpdated'; changes: Record<string, AnnotationBase | null> }

export type ToolMessage<A extends AnnotationBase, TS extends ToolStateBase> =
  | {
      type: 'createAnnotation'
      annotationDraft: A
      edit?: boolean
    }
  | { type: 'selectTool'; toolName: ToolStateBase['name'] }
  | { type: 'updateTool'; tool: TS }
  | { type: 'scrollToPage'; pageNumber: number }
  | { type: 'selectAnnotation'; annotationId: string }
  | { type: 'scrollToAnnotation'; annotationId: string }
  | { type: 'beginEditingAnnotation'; annotationDraft: A }
  | { type: 'createAnnotationDraft'; annotationDraft: A; elementStatus: ElementStatus }
  | { type: 'updateAnnotationDraft'; annotationDraft: A }
  | { type: 'discardAnnotationDraft'; annotationId: string }
  | { type: 'deselectAnnotation' }
  | { type: 'createAnnotation'; annotationDraft: A }
  | { type: 'updateAnnotation'; annotationDraft: A }
  | { type: 'deleteAnnotation'; annotationId: string }

export type DocumentViewerInit = {
  annotations: Record<string, AnnotationBase>
  plugins: AnnotationPlugin<AnnotationBase, ToolStateBase>[]
  initialTool: string
}

/**
 * Estimates the screen's effective logical DPI to align with native app rendering.
 *
 * This uses a heuristic since browsers don't expose physical PPI. It checks
 * the device's pixel ratio to determine if it's a HiDPI/Retina display.
 *
 * - Returns 113.5 for HiDPI screens (`devicePixelRatio > 1`), a common logical DPI
 *   for Retina devices (e.g., 227 physical PPI / 2x ratio).
 * - Returns 96 for standard screens, the default web standard.
 */
function guessScreenPixelsPerInch() {
  if (window.devicePixelRatio > 1) {
    return 227
  } else {
    return 96
  }
}

const POINTS_PER_INCH = 72

/**
 * to account for the screen's actual pixel density.
 *
 * This is done by checking the `devicePixelRatio`. If it's greater than 1,
 * we assume a HiDPI/Retina display and use an effective DPI of 113.5 to match
 * native PDF viewers. Otherwise, we fall back to the web standard of 96 DPI.
 */
function calculateCSSPixelsPerPDFPoint() {
  return guessScreenPixelsPerInch() / POINTS_PER_INCH / window.devicePixelRatio
}

export const CSS_PX_PER_PDF_PT = calculateCSSPixelsPerPDFPoint()

export const PAGE_VERTICAL_SPACING_PX = 36

// Height (in PDF points) that a page occupies once the user has "deleted" it.
// Chosen to be identical to the vertical-spacing constant so the placeholder
// visually collapses to roughly one spacer row.
export const PAGE_COLLAPSED_HEIGHT_PX = 24

export const documentViewerModule = defineModule<
  DocumentViewerState,
  DocumentViewerMessage,
  DocumentViewerInit
>({
  name: 'documentViewer',
  init: ({ annotations, plugins, initialTool }, { createRef }) => {
    const toolStates = plugins.reduce(
      (acc, plugin) => {
        acc[plugin.name] = plugin.initialToolState
        return acc
      },
      {} as Record<string, ToolStateBase>
    )

    return {
      zoom: 1.0,
      annotations: annotations,
      annotationDrafts: {},
      selectionState: { type: 'none' },
      selectedToolName: initialTool,
      toolStates: toolStates,
      plugins: createRef(plugins)
    }
  },
  handle: (state, message, { resolveRef }) => {
    const plugins = resolveRef(state.plugins)
    return docViewerReducer(state, message, plugins)
  }
})

function docViewerReducer(
  state: DocumentViewerState,
  message: DocumentViewerMessage,
  plugins: AnnotationPlugin<AnnotationBase, ToolStateBase>[]
): DocumentViewerState {
  switch (message.type) {
    case 'zoom':
      return { ...state, zoom: message.level }
    case 'selectAnnotation':
      const targetAnnotation = selAnnotationById(state, message.annotationId)

      if (!targetAnnotation) {
        return state
      }

      // If we select an annotation, we want to automatically select the relevant tool
      // for that annotation
      const matchingPlugin = plugins.find((plugin) => plugin.handlesAnnotation?.(targetAnnotation))
      const selectedToolName = matchingPlugin?.name ?? state.selectedToolName

      return {
        ...state,
        selectedToolName: selectedToolName,
        selectionState: {
          type: 'selected',
          annotationId: message.annotationId
        }
      }
    case 'deselectAnnotation':
      return { ...state, selectionState: { type: 'none' } }
    case 'beginEditingAnnotation':
      return {
        ...state,
        selectionState: {
          type: 'editing',
          annotationId: message.annotationDraft.id
        }
      }
    case 'createAnnotationDraft':
      return {
        ...state,
        selectionState:
          message.elementStatus == 'selected' || message.elementStatus == 'editing'
            ? { type: message.elementStatus, annotationId: message.annotationDraft.id }
            : { type: 'none' },
        annotationDrafts: {
          ...state.annotationDrafts,
          [message.annotationDraft.id]: message.annotationDraft
        }
      }
    case 'updateAnnotationDraft':
      return {
        ...state,
        annotationDrafts: {
          ...state.annotationDrafts,
          [message.annotationDraft.id]: message.annotationDraft
        }
      }
    case 'discardAnnotationDraft':
      var nextDrafts = { ...state.annotationDrafts }
      delete nextDrafts[message.annotationId]

      var selectionState: DocumentViewerState['selectionState'] =
        state.selectionState.type != 'none' &&
        state.selectionState.annotationId == message.annotationId
          ? { type: 'none' }
          : state.selectionState

      return { ...state, annotationDrafts: nextDrafts, selectionState: selectionState }
    case 'selectTool':
      var selectedAnnotation = selActiveAnnotation(state)
      var activePlugin = plugins.find((plugin) => plugin.name == message.toolName)

      // If we select a tool and the currently selected annotation is not handled by the tool,
      // we want to deselect the annotation to avoid confusion
      var shouldDeselect =
        selectedAnnotation && !activePlugin?.handlesAnnotation?.(selectedAnnotation)

      return {
        ...state,
        selectedToolName: message.toolName,
        selectionState: shouldDeselect ? { type: 'none' } : state.selectionState
      }
    case 'updateTool':
      return {
        ...state,
        toolStates: { ...state.toolStates, [message.tool.name]: message.tool }
      }
    case 'remoteAnnotationsUpdated':
      return reconcileAnnotationDraftsWithServerState({
        ...state,
        annotations: jsonMapPatch(state.annotations, message.changes)
      })
    case 'createAnnotation':
      return {
        ...state,
        annotationDrafts: {
          ...state.annotationDrafts,
          [message.annotationDraft.id]: message.annotationDraft
        }
      }
    case 'updateAnnotation':
      return {
        ...state,
        annotationDrafts: {
          ...state.annotationDrafts,
          [message.annotationDraft.id]: message.annotationDraft
        }
      }
    case 'deleteAnnotation': {
      // Optimistically hide the annotation by storing a tomb‑stone (`null`)
      // in the drafts map. Selectors interpret `null` as "already gone".
      return {
        ...state,
        selectionState: { type: 'none' },
        annotationDrafts: { ...state.annotationDrafts, [message.annotationId]: null }
      }
    }
    case 'scrollToPage':
    case 'scrollToAnnotation':
      return state
  }
}

export function selActiveAnnotationId(state: DocumentViewerState) {
  const selectionState = state.selectionState
  if (selectionState.type === 'none') return null
  return selectionState.annotationId
}

export function selActiveAnnotation(state: DocumentViewerState) {
  const activeAnnotationId = selActiveAnnotationId(state)
  if (!activeAnnotationId) return undefined

  const draft = state.annotationDrafts[activeAnnotationId]
  if (draft === null) return undefined // optimistically deleted
  return draft ?? state.annotations[activeAnnotationId]
}

export function selAnnotationById(state: DocumentViewerState, annotationId: string) {
  const draft = state.annotationDrafts[annotationId]
  if (draft === null) return undefined // optimistically deleted
  return draft ?? state.annotations[annotationId]
}

export function selAnnotationsForPage(state: DocumentViewerState, pageNumber: number) {
  return Object.values({
    ...state.annotations,
    ...state.annotationDrafts
  }).filter((annot) => annot !== null && annot.pageNumber == pageNumber)
}

export function selAnnotations<T extends AnnotationBase>(
  state: DocumentViewerState,
  predicate?: (annotation: AnnotationBase) => annotation is T
): Record<string, T> {
  // Merge persisted + draft annotations, skipping any tomb‑stones (`null`).
  const entries = Object.entries({
    ...state.annotations,
    ...state.annotationDrafts
  }).filter(([_, annotation]) => annotation !== null) as [string, AnnotationBase][]

  if (!predicate) {
    return Object.fromEntries(entries) as Record<string, T>
  }

  return Object.fromEntries(entries.filter(([_, annotation]) => predicate(annotation))) as Record<
    string,
    T
  >
}

/**
 * Returns the ids of annotations that are not persisted yet,
 * meaning that if they are discarded, they will be deleted from memory.
 */
export function selNotYetCreatedAnnotationDraftIds(state: DocumentViewerState) {
  const annotationDraftIds = Object.keys(state.annotationDrafts)
  return annotationDraftIds.filter((id) => {
    return state.annotationDrafts[id] !== null && state.annotations[id] === undefined
  })
}

/**
 * Reconciles the optimistic annotation drafts with the server state.
 */
function reconcileAnnotationDraftsWithServerState(state: DocumentViewerState) {
  const annotationDrafts = { ...state.annotationDrafts }
  for (const id of Object.keys(annotationDrafts)) {
    const annotation = state.annotations[id]
    const annotationDraft = annotationDrafts[id]

    // Drop tomb‑stones once the server has removed the annotation.
    if (annotationDraft === null && !annotation) {
      delete annotationDrafts[id]
      continue
    } else if (
      annotationDraft &&
      annotation &&
      deepEqual(dropMapKeys(annotation, ['time']), dropMapKeys(annotationDraft, ['time']))
    ) {
      delete annotationDrafts[id]
    }
  }
  return { ...state, annotationDrafts: annotationDrafts }
}

/**
 * Elixir-style Map.drop for plain JS objects.
 *
 * It clones the object once, removes the listed keys, and returns the clone.
 * No recursion, no surprises.
 */
export function dropMapKeys<T extends Record<string, unknown>, K extends keyof T = keyof T>(
  obj: T,
  keys: readonly K[]
): Omit<T, K> {
  const clone = { ...obj } as T
  for (const k of keys) {
    delete clone[k]
  }
  return clone as Omit<T, K>
}

export function selPageTransform({ zoom }: DocumentViewerState) {
  return LinearTransform.scale(zoom * CSS_PX_PER_PDF_PT)
}

/**
 * Calculate the y-coordinate of the scroll position needed to display a page in
 * the viewport.
 *
 * This makes use of the page dimensions, the linear transform applied (zoom, rotation, etc),
 * and the vertical spacing between pages to calculate where one would scroll to.
 *
 * @param documentStructure
 * @param linearTransform
 * @param pageNumber
 * @returns
 */
export function selScrollPagePositionY(
  pages: PageStructure[],
  linearTransform: LinearTransform,
  pageNumber: number,
  deletedPages: number[] = []
) {
  const deletedSet = new Set<number>(deletedPages)
  const transformedVerticalSpacing = LinearTransform.apply(
    { x: 0, y: PAGE_VERTICAL_SPACING_PX },
    linearTransform
  ).y

  // To scroll to a page, we sum the transformed heights of *preceding* pages.
  // For pages that have been soft-deleted, we use the collapsed-height constant.
  return pages
    .filter((page) => page.pageNumber < pageNumber)
    .reduce((acc, page) => {
      const baseHeightPx = deletedSet.has(page.pageNumber)
        ? PAGE_COLLAPSED_HEIGHT_PX
        : page.bounds.height

      const transformedPageHeight = LinearTransform.apply(
        { x: 0, y: baseHeightPx },
        linearTransform
      ).y

      return acc + transformedVerticalSpacing + transformedPageHeight
    }, 0)
}

/**
 * Calculate the scroll position (left, top) required to bring an *annotation*
 * into view inside the document-viewer scroll container.
 *
 * The returned point is in the same coordinate system as `scrollLeft`/`scrollTop` —
 * i.e. world coordinates after the current `linearTransform` has been applied.
 *
 * The helper is intentionally simple: it aligns the annotation's *top-left corner*
 * with the viewport's top-left corner.  Callers can adjust the result (e.g. to
 * centre the annotation) if they need a different anchoring.
 *
 * @param documentStructure Parsed page geometry for the whole PDF
 * @param linearTransform   Current zoom / rotation matrix
 * @param annotation        Any annotation that contains `pageNumber` & `bounds`
 * @param deletedPages      Page numbers collapsed by soft-deletion (optional)
 *
 * @returns A Point `{ x, y }` suitable for `scrollTo({ left:x, top:y })`
 */
export function selScrollAnnotationPosition(
  pages: PageStructure[],
  linearTransform: LinearTransform,
  annotation: AnnotationBase,
  deletedPages: number[] = []
): Point {
  // Vertical offset to the *top* of the annotation's page in world coords.
  const pageOffsetY = selScrollPagePositionY(
    pages,
    linearTransform,
    annotation.pageNumber,
    deletedPages
  )

  // Transform the annotation's own offset within the page.
  const transformed = LinearTransform.apply(
    { x: annotation.bounds.x, y: annotation.bounds.y },
    linearTransform
  )

  return {
    x: transformed.x,
    y: pageOffsetY + transformed.y
  }
}

/**
 * When the page transform changes, (ex a user zooms in or out),
 * the scroll position will get out of sync.
 *
 * What you were looking at will be in a different place.
 * This function calculates the new scroll position to account for the transform change.
 */
export function adjustScrollPosition(params: {
  prevTransform: LinearTransform // transform BEFORE change
  nextTransform: LinearTransform // transform AFTER  change
  prevScrollPosition: { x: number; y: number } // scroll BEFORE change
  anchorPoint: { x: number; y: number } // distance from viewport TL to anchor
}): { x: number; y: number } {
  const { prevTransform, nextTransform, prevScrollPosition, anchorPoint } = params

  // Screen → world coords (add current scroll origin)
  const anchorWorld: Point = {
    x: prevScrollPosition.x + anchorPoint.x,
    y: prevScrollPosition.y + anchorPoint.y
  }

  // World → document via inverse of the previous transform
  const anchorDoc = LinearTransform.apply(anchorWorld, LinearTransform.invert(prevTransform))

  // Document → world via the new transform
  const anchorWorldNext = LinearTransform.apply(anchorDoc, nextTransform)

  // World → scroll origin that puts anchor back at anchorOffset
  return {
    x: anchorWorldNext.x - anchorPoint.x,
    y: anchorWorldNext.y - anchorPoint.y
  }
}
